/**
 * API 클라이언트.
 *
 * 브라우저에서 그냥 열면 토큰이 없습니다. 그럴 때는 목업으로 떨어집니다 —
 * 디자인 확인용 미리보기가 계속 돌아가야 하기 때문입니다.
 * 어느 쪽인지는 화면에 배너로 드러냅니다. 조용히 가짜 데이터를 보여주면
 * 저장이 안 되는 이유를 아무도 모릅니다.
 */

const TOKEN_KEY = 'prayerlog.token';

/** 배포 시 주입됩니다. 비어 있으면 목업 모드로 동작합니다. */
export const API_BASE = import.meta.env.VITE_API_BASE ?? '';

/* ── 토큰 ────────────────────────────────────────────────────── */

/**
 * 셸은 토큰을 두 경로로 넘깁니다. 둘 중 하나만 살아도 로그인이 유지됩니다.
 *
 *  1. 주소 해시 `#t=<토큰>`  — 문서가 만들어지기 *전에* 이미 주소에 들어 있으므로
 *     스크립트 실행 타이밍에 걸리지 않습니다.
 *  2. localStorage 주입      — 새로고침·페이지 이동 뒤에도 남게 하는 쪽입니다.
 *
 * 해시 경로를 나중에 더한 이유가 있습니다. iOS WKWebView 에서
 * injectedJavaScriptBeforeContentLoaded 는 문서 출처가 확정되기 전에 돌 수 있고,
 * 그때 localStorage 접근이 SecurityError 로 막힙니다. 주입 스크립트는 그 예외를
 * 삼키므로 토큰이 조용히 사라지고 앱이 통째로 목업 모드가 됩니다.
 * 실제로 그 상태였습니다 — 로그인은 되는데 서버에 아무 요청도 가지 않았습니다.
 */
function readHashToken(): string | null {
  try {
    const found = /[#&]t=([^&]*)/.exec(window.location.hash);
    if (!found?.[1]) return null;
    const token = decodeURIComponent(found[1]);
    // 토큰이 주소창과 방문 기록에 남지 않게 즉시 지웁니다.
    // 해시는 서버로 전송되지 않으므로 접근 로그에는 애초에 남지 않습니다.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return token;
  } catch {
    return null;
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // 사파리 프라이빗 모드 등에서 localStorage 가 막힐 수 있습니다
    return null;
  }
}

/**
 * 모듈이 로드될 때 한 번 정한 뒤 메모리에 들고 갑니다.
 * localStorage 가 막힌 환경에서도 이 세션 동안은 로그인이 유지됩니다.
 */
let token: string | null = (() => {
  const fromHash = readHashToken();
  if (fromHash === null) return readStoredToken();
  try {
    localStorage.setItem(TOKEN_KEY, fromHash);
  } catch {
    // 저장에 실패해도 메모리에는 남으므로 이번 세션은 정상 동작합니다
  }
  return fromHash;
})();

export function getToken(): string | null {
  return token;
}

/**
 * 브라우저에서 로그인을 끝냈을 때 (화면 A1 — 웹).
 *
 * 셸이 있을 때는 셸이 심어 주지만, 셸 없이 열면 여기로 들어옵니다.
 * 메모리와 localStorage 둘 다에 둡니다 — 메모리는 이번 세션, localStorage 는
 * 새로고침 뒤를 위해서입니다.
 */
export function setToken(next: string): void {
  token = next;
  try {
    localStorage.setItem(TOKEN_KEY, next);
  } catch {
    // 저장에 실패해도 메모리에는 남으므로 이번 세션은 정상 동작합니다
  }
}

/** 로그아웃 — 화면에서 부릅니다 */
export function signOut(): void {
  clearToken();
}

/** 토큰이 죽었을 때. 다음 부팅에서 다시 심어 줍니다. */
function clearToken(): void {
  token = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 지우지 못해도 메모리에서는 사라졌습니다 */
  }
  listeners.forEach((fn) => fn());
}

/**
 * 토큰이 사라졌다는 것을 화면에 알립니다.
 *
 * 네이티브 셸이 있을 때는 셸이 A1 으로 되돌려 주지만, 브라우저에는 그럴
 * 셸이 없습니다. 알리지 않으면 30일 뒤 토큰이 만료됐을 때 화면이 오류만
 * 뱉으며 남아 있고, 새로고침해야 로그인 화면으로 돌아옵니다.
 */
type Listener = () => void;
const listeners = new Set<Listener>();

export function onSignedOut(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * 지금 어떤 모드인가. 목업이면 *왜* 목업인지까지 돌려줍니다.
 * 기기에서 문제를 볼 때 원인을 추측하지 않아도 되게 하려는 것입니다.
 */
export type LiveStatus = 'live' | 'no-token' | 'no-api';

export function liveStatus(): LiveStatus {
  if (API_BASE === '') return 'no-api';
  if (token === null) return 'no-token';
  return 'live';
}

/** 실제 API 를 쓸 수 있는 상태인가 */
export function isLive(): boolean {
  return liveStatus() === 'live';
}

export class ApiError extends Error {
  // 생성자 파라미터 프로퍼티는 erasableSyntaxOnly 에서 못 씁니다
  // (타입만 지워서는 JS 로 안 남기 때문). 필드로 따로 둡니다.
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const bearer = getToken();
  if (!bearer) throw new ApiError(401, '로그인이 필요합니다.');

  const headers: Record<string, string> = { Authorization: `Bearer ${bearer}` };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    });
  } catch {
    throw new ApiError(0, '서버에 연결하지 못했습니다. 네트워크를 확인해 주세요.');
  }

  if (res.status === 204) return undefined as T;

  const data = (await res.json().catch(() => ({}))) as { error?: string } & T;

  if (!res.ok) {
    if (res.status === 401) {
      // 토큰이 죽었습니다. 죽은 토큰을 들고 계속 재시도하지 않게 먼저 버리고,
      // 셸과 화면 양쪽에 알려 로그인 화면으로 되돌립니다 — 셸이 없는
      // 브라우저에서는 clearToken 안의 알림이 그 일을 합니다.
      clearToken();
      notifyShell({ type: 'signOut', reason: 'expired' });
    }
    throw new ApiError(res.status, data.error ?? `요청에 실패했습니다. (${res.status})`);
  }

  return data;
}

/* ── 네이티브 셸과의 통신 ─────────────────────────────────────── */

interface ShellMessage {
  type: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    PrayerLogBridge?: { post: (msg: ShellMessage) => void };
  }
}

/** 셸이 없으면(브라우저) 조용히 무시합니다. */
export function notifyShell(msg: ShellMessage): void {
  window.PrayerLogBridge?.post(msg);
}
